<template>
    <div
        :class="classQuote"
        class="quote"
    >
        <div class="quote-wrapper">
            <div class="quote-content">
                {{ data.quote }}

                <p
                    v-if="data.author"
                    class="author"
                >
                    — {{ data.author }}
                </p>
            </div>
        </div>

        <div
            v-if="data.hasBackgroundImage && data.quoteImage"
            class="image-wrapper"
        >
            <CmsImage
                :data="data.quoteImage"
                fit="cover"
                :sizes="['100vw']"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { ComponentQuoteFragment } from '@/graphql/api'
import CmsImage from '@/components/cms/CmsImage.vue'

const props = defineProps<{
    data: ComponentQuoteFragment
}>()

const classQuote = computed(() => ({
    [props.data.size.toLocaleLowerCase()]: true,
    'with-image': props.data.hasBackgroundImage,
    'with-quotes': props.data.showQuotationMarks,
}))
</script>

<style lang="scss" scoped>
.quote {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    color: $slate;

    @include min-width(768) {
        padding: 80px 20px;
    }

    .quote-wrapper {
        width: 1004px;
        max-width: 100%;
        margin: 0 auto;
        position: relative;
        z-index: 3;
        font-size: 1.5rem;
        line-height: 1.5em;
        border-top: 4px solid $slate;
        padding: 20px 20px 40px;

        @include min-width(768) {
            border-top: none;
            padding: 0 0 0 20px;
            border-left: 4px solid $slate;
        }

        .author {
            font-size: 1rem;
            font-style: italic;
            display: block;
            margin: 10px 0 0;
            width: 100%;
        }
    }

    &.large {
        .quote-wrapper {
            font-size: 2.5rem;
            line-height: 1.25em;
        }
    }

    &.with-image {
        @include min-width(768) {
            padding: 100px 20px;
            min-height: 450px;
            color: $white;
        }

        .quote-wrapper {
            @include min-width(768) {
                border-color: $white;
            }
        }

        &.with-quotes {
            .quote-wrapper {
                border-left: none;

                @include min-width(768) {
                    padding: 0;
                }

                .quote-content {
                    @include min-width(768) {
                        border-color: $white;
                    }
                }

                &::before {
                    @include min-width(768) {
                        background-image: url('/src/assets/icons/quotation-mark-white.svg');
                    }
                }
            }
        }
    }

    &.with-quotes {
        .quote-wrapper {
            border-left: none;

            @include min-width(768) {
                display: flex;
                align-items: center;
            }

            .quote-content {
                align-self: stretch;
                display: flex;
                align-items: center;
                flex-wrap: wrap;

                @include min-width(768) {
                    border-left: 4px solid $slate;
                    padding: 0 0 0 20px;
                }
            }

            &::before {
                content: '';
                background: url('/src/assets/icons/quotation-mark-red.svg')
                    no-repeat center center;
                background-size: contain;
                width: 110px;
                height: 110px;
                display: block;
                margin: 0 20px 20px;
                flex-shrink: 0;

                @include min-width(768) {
                    margin: 0 20px 0 0;
                }
            }
        }
    }

    .image-wrapper {
        display: none;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;

        @include min-width(768) {
            display: block;
        }

        &::before {
            content: '';
            display: block;
            background: rgba($black, 0.2);
            width: 100%;
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
            z-index: 2;
        }
    }
}
</style>
